import React, { useCallback, useState } from "react";

import { faPlus, faTools } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { FlowToolFlowSelectionModal } from "src/agents/AgentVersionPage/AgentToolsEditor/FlowToolFlowSelectionModal";
import { useSideDrawerCoordinator } from "src/agents/base/SideDrawer";
import { ToolCard, ToolType } from "src/agents/base/ToolCard";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { Flow, FlowVersion, Schema } from "src/api/flowTypes";
import { Button } from "src/base-components/Button";
import {
  AgentVersionConfigurationInputToolsInner,
  MetadataOverlay,
} from "src/clients/agent-management";
import { EmptyState } from "src/design-system/EmptyState";

type AgentToolsEditorProps = {
  immutable: boolean;
};

/**
 * Build metadata_overlay from I/O schema, preserving existing descriptions
 * when properties still exist in the new schema.
 */
export const buildMetadataOverlay = (
  schema?: Schema,
  existingOverlay?: MetadataOverlay,
): MetadataOverlay => {
  const properties = schema?.properties ?? {};

  const overlayProperties: Record<string, { description?: string }> = {};

  for (const propName of Object.keys(properties)) {
    const existingDescription =
      existingOverlay?.properties?.[propName]?.description;
    overlayProperties[propName] = {
      description: existingDescription ?? "",
    };
  }

  return { properties: overlayProperties };
};

/**
 * Build a new tool from a selected flow and version.
 */
export const buildNewTool = (
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(version.input_schema);
  const outputMetadataOverlay = buildMetadataOverlay(version.output_schema);

  return {
    id: uuidv4(),
    type: "flow",
    name: flow.name,
    description: flow.meta.description ?? "",
    flow_slug: flow.slug,
    flow_version_id: version.id,
    output_type: "json",
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
    input_schema: {},
    output_schema: {},
  };
};

/**
 * Update an existing tool with a new flow/version selection.
 */
export const buildUpdatedTool = (
  existingTool: AgentVersionConfigurationInputToolsInner,
  flow: Flow,
  version: FlowVersion,
): AgentVersionConfigurationInputToolsInner => {
  const inputMetadataOverlay = buildMetadataOverlay(
    version.input_schema,
    existingTool.input_schema_metadata_overlay,
  );
  const outputMetadataOverlay = buildMetadataOverlay(
    version.output_schema,
    existingTool.output_schema_metadata_overlay,
  );

  return {
    ...existingTool,
    flow_slug: flow.slug,
    flow_version_id: version.id,
    input_schema_metadata_overlay: inputMetadataOverlay,
    output_schema_metadata_overlay: outputMetadataOverlay,
  };
};

export const AgentToolsEditor: React.FC<AgentToolsEditorProps> = ({
  immutable,
}) => {
  const { setValue, getValues, control } =
    useFormContext<AgentVersionConfigurationInput>();
  const tools = useWatch({ name: "tools", control });
  // Drawer coordinator
  const { requestOpen, requestClose, openDrawer } = useSideDrawerCoordinator();

  // Flow selection modal state - uses id for existing tools
  const [flowModalState, setFlowModalState] = useState<
    { type: "new" } | { type: "existing"; toolId: string } | null
  >(null);

  // Open the config drawer for a tool by its id
  const openToolConfigDrawer = useCallback(
    (toolId: string) => {
      requestOpen({
        type: "flowToolConfig",
        data: {
          toolId,
          onRequestChangeFlow: () =>
            setFlowModalState({ type: "existing", toolId }),
        },
      });
    },
    [requestOpen],
  );

  // Handle flow/version selection from modal
  const handleFlowVersionSelected = useCallback(
    (flow: Flow, version: FlowVersion) => {
      // Get fresh tools value to avoid stale closure
      const currentTools = getValues("tools") ?? [];

      if (flowModalState?.type === "new") {
        const newTool = buildNewTool(flow, version);

        setValue("tools", [...currentTools, newTool]);
        openToolConfigDrawer(newTool.id);
      } else if (flowModalState?.type === "existing") {
        const { toolId } = flowModalState;
        const tool = currentTools.find((f) => f.id === toolId);
        if (tool) {
          const updatedTool = buildUpdatedTool(tool, flow, version);
          // Replace entire array to ensure re-render
          setValue(
            "tools",
            currentTools.map((t) => (t.id === toolId ? updatedTool : t)),
          );
          openToolConfigDrawer(toolId);
        }
      }
      setFlowModalState(null);
    },
    [flowModalState, setValue, getValues, openToolConfigDrawer],
  );

  // Handle deleting a tool
  const handleDeleteTool = useCallback(
    (toolId: string) => {
      if (
        openDrawer?.type === "flowToolConfig" &&
        openDrawer.data?.toolId === toolId
      ) {
        requestClose();
      }
      const currentTools = getValues("tools") ?? [];
      setValue(
        "tools",
        currentTools.filter((t) => t.id !== toolId),
      );
    },
    [requestClose, setValue, getValues, openDrawer],
  );

  // Handle clicking "+ Add tool" button
  const handleAddTool = useCallback(() => {
    requestOpen({
      type: "addTool",
      data: {
        onAddFlowTool: () => setFlowModalState({ type: "new" }),
      },
    });
  }, [requestOpen]);

  // Handle flow selection modal close
  const handleFlowModalClose = useCallback(() => {
    setFlowModalState(null);
  }, []);

  return (
    <>
      <div>
        {!tools || tools.length === 0 ? (
          <EmptyState
            actionButton={
              immutable
                ? undefined
                : {
                    text: "Add tool",
                    icon: faPlus,
                    onClick: handleAddTool,
                  }
            }
            description="Add tools for the agent to use when processing requests"
            headline="No tools configured"
            icon={faTools}
          />
        ) : (
          <div className="relative rounded-lg bg-gray-50 p-5 pr-8">
            {!immutable && (
              <div className="absolute -top-10 right-0">
                <Button
                  dataLoc="add-tool-button"
                  size="sm"
                  variant="secondary"
                  onClick={handleAddTool}
                >
                  + Add tool
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-4">
              {tools.map((tool) => {
                const isSelected =
                  openDrawer?.type === "flowToolConfig" &&
                  openDrawer.data?.toolId === tool.id;
                return (
                  <ToolCard
                    key={tool.id}
                    dataLoc={`tool-card-${tool.id}`}
                    description={tool.description}
                    name={tool.name}
                    selected={isSelected}
                    type={tool.type as ToolType}
                    onClick={() => openToolConfigDrawer(tool.id)}
                    onDelete={
                      immutable ? undefined : () => handleDeleteTool(tool.id)
                    }
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Flow selection modal - rendered here so AgentToolsEditor owns its state */}
      <FlowToolFlowSelectionModal
        open={flowModalState !== null}
        onClose={handleFlowModalClose}
        onSelect={handleFlowVersionSelected}
      />
    </>
  );
};
